// Catch-all logging entry point. The quick-log grid is intentionally short
// — this sheet lists every event type, so anything not on the grid (or any
// historical backfill) is still one tap away.

import type { ReactNode } from 'react';
import { Icon } from '../Icon.tsx';
import { SheetShell } from './SheetShell.tsx';

export type AnyEventKind =
  | 'sleep'
  | 'breast'
  | 'bottle'
  | 'solid'
  | 'pump'
  | 'diaper'
  | 'measurement'
  | 'milestone'
  | 'medication'
  | 'temperature';

type Props = {
  open: boolean;
  onClose: () => void;
  onPick: (kind: AnyEventKind) => void;
};

type Option = {
  id: AnyEventKind;
  icon: ReactNode;
  accent: string;
  title: string;
  subtitle: string;
};

const OPTIONS: Option[] = [
  { id: 'sleep',       icon: <Icon.moon s={20} />,    accent: 'sleep-night', title: 'Sleep',       subtitle: 'Nap, night, location' },
  { id: 'breast',      icon: <Icon.breast s={20} />,  accent: 'breast',      title: 'Breast feed', subtitle: 'Left, right, switch, pause' },
  { id: 'bottle',      icon: <Icon.bottle s={20} />,  accent: 'bottle',      title: 'Bottle',      subtitle: 'Expressed, formula, ml' },
  { id: 'solid',       icon: <Icon.solid s={20} />,   accent: 'solid',       title: 'Solid',       subtitle: 'Food intake' },
  { id: 'pump',        icon: <Icon.pump s={20} />,    accent: 'pump',        title: 'Pump',        subtitle: 'Yield by side' },
  { id: 'diaper',      icon: <Icon.diaper s={20} />,  accent: 'pee',         title: 'Diaper',      subtitle: 'Pee or poop' },
  { id: 'measurement', icon: <Icon.chart s={20} />,   accent: 'accent',      title: 'Measurement', subtitle: 'Weight, length, head' },
  { id: 'milestone',   icon: <Icon.sparkle s={20} />, accent: 'breast',      title: 'Milestone',   subtitle: 'First smile, first crawl…' },
  { id: 'medication',  icon: <Icon.bottle s={20} />,  accent: 'danger',      title: 'Medication',  subtitle: 'Vitamin D, paracetamol…' },
  { id: 'temperature', icon: <Icon.chart s={20} />,   accent: 'danger',      title: 'Temperature', subtitle: 'Daily check or sick mode' },
];

export function AddAnythingSheet({ open, onClose, onPick }: Props) {
  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title="Log anything"
      tagline="What happened?"
      accent="accent"
      icon={(p) => <Icon.plus {...p} />}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
        }}
      >
        {OPTIONS.map((o) => (
          <button
            type="button"
            key={o.id}
            onClick={() => {
              onPick(o.id);
              onClose();
            }}
            style={{
              all: 'unset',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              padding: '14px 12px',
              borderRadius: 16,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                background: `color-mix(in oklab, var(--${o.accent}) 18%, transparent)`,
                color: `var(--${o.accent})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {o.icon}
            </div>
            <div className="lt-serif" style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-1)' }}>
              {o.title}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.3 }}>
              {o.subtitle}
            </div>
          </button>
        ))}
      </div>
    </SheetShell>
  );
}
